import clsx from 'clsx';
import React, {
  ChangeEvent,
  cloneElement,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  HTMLProps,
  isValidElement,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { useUniqueId } from '../../hooks/use-unique-id';
import { Hint } from '../hint';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { InputLabel, InputLabelProps } from '../input-label';
import { SeparatedList } from '../separated-list';
import { Typography } from '../typography';
import classes from './input-wrapper.module.scss';

export type InputWrapperInputListeners = {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export type InputWrapperInputAccessibility = Pick<HTMLProps<HTMLInputElement>,
  'aria-invalid' |
  'aria-describedby' |
  'aria-errormessage'>;

export type InputWrapperInputElementProps<T> =
  Pick<HTMLProps<HTMLInputElement>,
    'id' |
    'disabled' |
    'type' |
    'aria-labelledby' |
    'placeholder' |
    'name' |
    'required' |
    'className' |
    'autoFocus'>
  & {
    ref: ForwardedRef<HTMLInputElement>;
  } & (T extends File ? { type: 'file' } : (T extends File[] ? { type: 'file' } : { value: T }));

export type InputWrapperInputProps<T = string> = {
  listeners: InputWrapperInputListeners;
  input: InputWrapperInputElementProps<T>;
  accessibility: InputWrapperInputAccessibility;
  id: string;
};

export type InputWrapperProps<T = string> = {
  children?: ReactNode | ((props: InputWrapperInputProps<T>) => ReactNode);

  /**
   * Custom rendered label above the input field
   */
  label?: ReactNode;

  /**
   * Prefix item within the input field. Used for Icons or similar
   */
  prefix?: ReactElement;

  /**
   * Suffix item within the input field. Used for Icons or similar
   */
  suffix?: ReactElement;

  /**
   * Custom placeholder text
   */
  placeholder?: string;

  /**
   * Input fields value
   */
  value?: T;

  /**
   * Makes the input clearable
   */
  clearable?: boolean;

  /**
   * Custom event handler when input changes
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

  /**
   * A callback function that is triggered whenever the value changes.
   */
  onChangeValue?: (value: T) => void;

  /**
   * HTML element name for forms support
   */
  name?: string;

  /**
   * Render input as disabled
   */
  disabled?: boolean;

  /**
   * Add helper text below the input field
   */
  helper?: ReactNode;

  /**
   * Render in error state
   */
  error?: boolean;

  /**
   * Custom input props which will be bound to the `input` element
   */
  inputProps?: HTMLProps<HTMLInputElement | HTMLTextAreaElement>;

  /**
   * Custom variant of the adornment styling
   */
  adornmentVariant?: 'inline' | 'cell';

  /**
   * Custom prefix wrapper properties
   */
  prefixProps?: HTMLProps<HTMLDivElement>;

  /**
   * Props for the Suffix wrapper component.
   */
  suffixProps?: HTMLProps<HTMLDivElement>;

  /**
   * Custom classes
   */
  classes?: {
    inputWrapper?: string;
    prefix?: string;
    suffix?: string;
    input?: string;
    wrapper?: string;
    label?: string;
    description?: string;
    helper?: string;
  };

  /**
   * Customize input type
   */
  type?:
  | 'number'
  | 'tel'
  | 'email'
  | 'password'
  | 'file'
  | 'date'
  | 'time'
  | 'url'
  | 'text'
  | 'color'
  | 'datetime-local';

  onEnterKey?: (e: KeyboardEvent<HTMLInputElement>) => void;

  /**
   * Specifies whether the element should automatically gain focus when it is mounted.
   * If set to true, the element will receive focus as soon as it is rendered.
   * If set to false or not specified, the element will not automatically gain focus.
   *
   * @type {boolean}
   */
  autoFocus?: boolean;

  /**
   * Callback function that is called when the "onClear" event occurs.
   */
  onClear?: () => void;

  /**
   * Description text below the label
   */
  description?: ReactNode;

  /**
   * Hint text next the label
   */
  hint?: ReactNode;

  rootRef?: (element: HTMLDivElement) => void;

  /**
   * An optional property that allows customization of the input label's props.
   * Users can provide a partial configuration of `InputLabelProps` to override or extend
   * the default label properties. Useful for adapting the label's appearance or behavior
   * to specific requirements.
   *
   * This property is optional.
   */
  labelProps?: Partial<InputLabelProps>;

  /**
   * Represents the CSS class name(s) to style a component or element.
   * This property allows passing one or more class names that can be used
   * to customize the appearance through external or inline styles.
   * It is an optional property.
   */
  className?: string;

  /**
   * Indicates whether the field or property is required.
   *
   * If set to `true`, the field or property must be provided or will be considered invalid.
   * If set to `false` or undefined, the field or property is optional.
   */
  required?: boolean;
  rootProps?: Partial<HTMLProps<HTMLDivElement>>;
};

const InputWrapperInner = <T = string>(
  {
    label,
    name,
    helper,
    prefix,
    suffix,
    inputProps,
    error,
    placeholder,
    disabled,
    value,
    onChange,
    onChangeValue,
    adornmentVariant = 'inline',
    type = 'text',
    onEnterKey,
    clearable,
    prefixProps,
    suffixProps,
    autoFocus,
    classes: customClasses,
    onClear,
    children,
    required,
    description,
    hint,
    className,
    rootRef,
    labelProps,
    rootProps,
    ...props
  }: InputWrapperProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const { t } = useTranslation();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const labelId = useUniqueId('label-', inputProps?.id);
  const inputId = useUniqueId('input-', inputProps?.id);
  const helperId = useUniqueId('helper-');

  const [prefixRef, prefixMeasure] = useMeasure<HTMLDivElement>();
  const [suffixRef, suffixMeasure] = useMeasure<HTMLDivElement>();

  const onClearClicked = useCallback(() => {
    if (typeof value === 'string') {
      onChangeValue?.('' as T);
      onChange?.({
        target: {
          value: ''
        }
      } as ChangeEvent<HTMLInputElement>);
    }
    onClear?.();
  }, [onClear, onChangeValue]);

  const hasValue = useMemo(() => {
    if (typeof value === 'string') {
      return !!value;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return !!value;
  }, [value]);

  const listeners = useMemo(() => ({
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onChangeValue?.(e.target.value as T);
      inputProps?.onChange?.(e);
    },
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      inputProps?.onKeyDown?.(event);
      if (event.key === 'Enter') {
        onEnterKey?.(event);
      }
    }
  }), [onChange, onChangeValue, inputProps?.onChange, inputProps?.onKeyDown, onEnterKey]);


  const inputProps2 = useMemo(() => ({
    ...props,
    ...inputProps,
    id: inputId,
    ref,
    disabled,
    type: type === 'password' ? (isPasswordVisible ? 'text' : 'password') : type,
    ...(type !== 'file' ? { value } : {}),
    ...(['email', 'number', 'password', 'search', 'tel', 'text', 'url'].includes(type) ? { placeholder } : {}),
    ...(!label ? { 'aria-label': placeholder } : { 'aria-labelledby': labelId }),
    name,
    required,
    autoFocus,
    className: clsx(isValidElement(children) ? children.props.className : '', classes.input, inputProps?.className, customClasses?.input)
  } as unknown as InputWrapperInputElementProps<T>), [ref, inputId, disabled, type, isPasswordVisible, value, placeholder, label, name, required, autoFocus, children, classes.input, inputProps?.className, customClasses?.input, error, helperId, helper, labelId, isValidElement(children)]);

  const inputAccessibility: InputWrapperInputAccessibility = useMemo(() => ({
    'aria-invalid': error,
    'aria-describedby': !error && helper ? helperId : undefined,
    'aria-errormessage': error ? helperId : undefined
  }), [error, helperId, helper]);

  const renderProps = useMemo(() => ({
    id: inputId,
    ...inputProps,
    ...(isValidElement(children) ? children?.props : {})
  }), [inputId, inputProps, children, children]);


  return (
    <div
      {...rootProps}
      data-testid={rootProps?.['data-testid'] ?? props['data-testid'] ?? 'input-wrapper'}
      style={
        {
          ...rootProps?.style,
          '--input-wrapper--prefix-width': `${prefixMeasure.right}px`,
          '--input-wrapper--suffix-width': `${suffixMeasure.width + 16}px`
        } as CSSProperties
      }
      className={clsx(classes.root, 'input-wrapper-component', rootProps?.className, className, classes[`type_${type}`], {
        [classes.hasPrefix]: !!prefix,
        [classes.hasSuffix]: !!suffix,
        [classes.hasError]: error,
        [classes.disabled]: disabled,
        [classes.adornmentCell]: adornmentVariant === 'cell'
      })}
    >
      <div className={clsx(classes.wrapper, customClasses?.wrapper)}>
        {label && (
          <InputLabel
            {...labelProps}
            id={labelId}
            hint={hint ? <Hint>{hint}</Hint> : undefined}
            required={required}
            htmlFor={inputId}
            className={customClasses?.label}
          >
            {label}
          </InputLabel>
        )}

        {description
          && (
            <Typography
              className={clsx(classes.description, customClasses?.description)}
              variant="caption"
            >
              {description}
            </Typography>
          )}
        <div
          className={clsx(classes.inputWrapper, customClasses?.inputWrapper)}
          ref={r => {
            rootRef?.(r!);
          }}
        >
          {prefix && (
            <div
              ref={prefixRef}
              {...prefixProps}
              className={clsx(
                classes.prefix,
                prefixProps?.className,
                customClasses?.prefix
              )}
            >
              {prefix}
            </div>
          )}
          <div className={classes.inputContent}>
            {typeof children === 'function' && children({
              id: inputId,
              listeners,
              accessibility: inputAccessibility,
              input: inputProps2
            })}
            {isValidElement(children) && cloneElement(children, {
              ...children.props,
              ...renderProps,
              ...inputAccessibility,
              ...inputProps2,
              ...listeners
            })}
          </div>

          {type === 'password' && (
            <div
              ref={suffixRef}
              className={clsx(classes.suffix,
                customClasses?.suffix)}
            >
              <div>
                <IconButton
                  color="dark-grey"
                  data-testid="visibility-toggle"
                  label={t(isPasswordVisible ? 'common.hide-password' : 'common.show-password')}
                  variant="plain"
                  onClick={() => setPasswordVisible(!isPasswordVisible)}
                >
                  <Icon icon={['far', isPasswordVisible ? 'eye-slash' : 'eye']} />
                </IconButton>
              </div>
            </div>
          )}

          {type !== 'password' && (clearable || !!suffix) && (
            <div
              ref={suffixRef}
              {...suffixProps}
              className={clsx(
                classes.suffix,
                suffixProps?.className,
                customClasses?.suffix
              )}
            >
              <SeparatedList spacing={0.5}>
                {clearable && hasValue && (
                  <IconButton
                    label={t('common.remove')}
                    color="grey"
                    variant="text"
                    data-testid="clear"
                    onClick={onClearClicked}
                  >
                    <Icon icon="times" />
                  </IconButton>
                )}

                {suffix}
              </SeparatedList>
            </div>
          )}
        </div>
        {helper && (
          <Typography id={helperId} className={clsx(classes.helper, customClasses?.helper)} data-testid={`${rootProps?.['data-testid'] ?? props['data-testid'] ?? 'input-wrapper'}-helper`} variant="caption">
            {helper}
          </Typography>
        )}
      </div>
    </div>
  );
};

export const InputWrapper = forwardRef(InputWrapperInner) as <T>(
  props: InputWrapperProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof InputWrapperInner>;